import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { AtSign, KeyRound, Briefcase, QrCode, Smartphone } from 'lucide-react'
import { getAuth, sendPasswordResetEmail } from 'firebase/auth'
import { doc, getDoc } from 'firebase/firestore'
import { getFirestoreDb } from '@/lib/firebase'
import { useAuth } from '@/contexts/AuthContext'
import { usernameToEmail, KULLANICI_DOMAIN } from '@/lib/username'
import { QrScannerModal } from '@/components/qr/QrScannerModal'

/** Kurulum QR içeriğinden token'ı çıkarır (URL, ?token=..., veya düz token). */
function tokenCikar(icerik: string): string | null {
  const s = icerik.trim()
  try {
    const u = new URL(s)
    const t = u.searchParams.get('token')
    if (t) return t
  } catch {
    // URL değil, devam
  }
  const m = s.match(/[?&]token=([^&\s]+)/)
  if (m && m[1]) return decodeURIComponent(m[1])
  if (s.length > 0 && !s.includes(' ')) return s
  return null
}

export function LoginPage() {
  const navigate = useNavigate()
  const { firebaseUser, kullanici } = useAuth()

  // 🔑 Login state hazır olunca otomatik yönlendir (race-free)
  useEffect(() => {
    if (firebaseUser && kullanici) {
      navigate('/', { replace: true })
    }
  }, [firebaseUser, kullanici, navigate])

  return (
    <div className="page-container bg-gradient-to-br from-brand-50 via-white to-indigo-50">
      <div className="max-w-md mx-auto px-4 py-8">
        <div className="text-center mb-8 mt-4">
          <h1 className="text-3xl font-bold text-gray-900">Shift Beat</h1>
          <p className="text-sm text-gray-500 mt-1">Personel Sistemi</p>
        </div>

        <div className="card animate-slide-up">
          <YoneticiLoginForm />
        </div>

        <PersonelKurulumKarti />
      </div>
    </div>
  )
}

function PersonelKurulumKarti() {
  const navigate = useNavigate()
  const [scannerAcik, setScannerAcik] = useState(false)
  const [hata, setHata] = useState<string | null>(null)

  function qrOkundu(icerik: string) {
    setScannerAcik(false)
    const token = tokenCikar(icerik)
    if (!token) {
      setHata("QR okunamadı ya da kurulum QR'ı değil. Yöneticinden doğru QR'ı iste.")
      return
    }
    // Uygulama içi yönlendirme — oturum uygulamanın kendi deposuna yazılır
    navigate('/kurulum?token=' + encodeURIComponent(token))
  }

  return (
    <div className="card mt-4">
      <div className="text-center space-y-3">
        <div className="bg-brand-50 p-3 rounded-2xl inline-block">
          <Smartphone className="w-8 h-8 text-brand-600" />
        </div>
        <h2 className="font-bold text-base text-gray-900">Personel misin?</h2>
        <p className="text-sm text-gray-500">
          Telefonunu ilk kez bağlıyorsan, yöneticinden aldığın kurulum QR'ını buradan okut.
        </p>
        <div className="bg-amber-50 border border-amber-200 text-amber-900 text-xs rounded-xl p-2.5 text-left">
          📱 iPhone'da: önce bu uygulamayı <strong>Ana Ekrana Ekle</strong>, sonra uygulamayı
          açıp kurulumu <strong>buradan</strong> yap. (Kamerayla dışarıdan okutursan giriş kalıcı olmaz.)
        </div>
        {hata && (
          <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
            {hata}
          </div>
        )}
        <button
          type="button"
          onClick={() => { setHata(null); setScannerAcik(true) }}
          className="btn-secondary w-full flex items-center justify-center gap-2"
        >
          <QrCode className="w-4 h-4" />
          Kurulum QR'ını Oku
        </button>
      </div>

      <QrScannerModal
        acik={scannerAcik}
        onKapat={() => setScannerAcik(false)}
        onOkundu={qrOkundu}
        aciklama="Yöneticinden aldığın kurulum QR'ını kameraya tut."
      />
    </div>
  )
}

function YoneticiLoginForm() {
  const { girisYap } = useAuth()
  const [kullaniciAdi, setKullaniciAdi] = useState('')
  const [sifre, setSifre] = useState('')
  const [hata, setHata] = useState<string | null>(null)
  const [yukleniyor, setYukleniyor] = useState(false)
  const [sifreUnutMesaj, setSifreUnutMesaj] = useState<string | null>(null)
  const [sifreUnutGonderiliyor, setSifreUnutGonderiliyor] = useState(false)

  const [sifreModalAcik, setSifreModalAcik] = useState(false)
  const [sifreModalEmail, setSifreModalEmail] = useState('')

  function sifremiUnuttumAc() {
    setHata(null)
    setSifreUnutMesaj(null)
    setSifreModalEmail('')
    setSifreModalAcik(true)
  }

  async function sifremiUnuttumGonder() {
    setHata(null)
    setSifreUnutMesaj(null)
    const temizEmail = sifreModalEmail.trim()
    if (!temizEmail.includes('@')) {
      setHata('Geçerli bir e-posta gir.')
      return
    }
    setSifreUnutGonderiliyor(true)
    try {
      const auth = getAuth()
      await sendPasswordResetEmail(auth, temizEmail)
      setSifreUnutMesaj(
        `Sıfırlama linki gönderildi. Gelen kutunu (ve spam'i) kontrol et.`
      )
      setSifreModalAcik(false)
    } catch (err) {
      const kod = (err as { code?: string }).code
      if (kod === 'auth/invalid-email') {
        setHata('Geçerli bir e-posta adresi gir.')
      } else if (kod === 'auth/too-many-requests') {
        setHata('Çok fazla deneme yaptın, biraz bekle.')
      } else {
        // Güvenlik için aynı mesaj (kullanıcı var/yok belli olmasın)
        setSifreUnutMesaj(
          `Sıfırlama linki gönderildi (eğer hesap kayıtlıysa). Gelen kutunu kontrol et.`
        )
        setSifreModalAcik(false)
      }
    } finally {
      setSifreUnutGonderiliyor(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setHata(null)
    setYukleniyor(true)

    try {
      let email: string
      const girdi = kullaniciAdi.trim().toLowerCase()
      if (girdi.includes('@')) {
        email = girdi
      } else {
        // Önce loginAliases'ta kısa ad ara (BOSS kısaltması gibi)
        let aliasEmail: string | null = null
        try {
          const snap = await getDoc(doc(getFirestoreDb(), 'loginAliases', girdi))
          if (snap.exists()) aliasEmail = (snap.data()?.email ?? null) as string | null
        } catch {
          // alias okunamadıysa sessiz geç, fallback'e düş
        }
        email = aliasEmail ?? usernameToEmail(kullaniciAdi)
      }
      await girisYap(email, sifre)
      // ✅ Navigate burada YOK — LoginPage'deki useEffect AuthContext yüklenince
      // otomatik yönlendirir. Race condition'ı önler.
      // Yukleniyor=true kalır, redirect olunca component unmount olur.
    } catch (err) {
      const errorCode = (err as { code?: string }).code

      if (errorCode === 'auth/invalid-credential' || errorCode === 'auth/wrong-password') {
        setHata('E-posta veya şifre hatalı.')
      } else if (errorCode === 'auth/user-not-found') {
        setHata('Bu e-postaya kayıtlı kullanıcı bulunamadı.')
      } else if (errorCode === 'auth/too-many-requests') {
        setHata('Çok fazla başarısız deneme. Bir süre bekleyin.')
      } else if (errorCode === 'auth/network-request-failed') {
        setHata('İnternet bağlantınızı kontrol edin.')
      } else if (errorCode === 'auth/invalid-email') {
        setHata('Geçerli bir e-posta adresi girin.')
      } else {
        setHata('Giriş yapılamadı. Lütfen tekrar deneyin.')
      }

      setYukleniyor(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="text-center mb-4">
        <Briefcase className="w-12 h-12 text-brand-600 mx-auto mb-2" />
        <h2 className="font-bold text-lg">Yönetici Girişi</h2>
        <p className="text-sm text-gray-500">BOSS veya yetkili personel</p>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Kullanıcı Adı
        </label>
        <div className="relative">
          <AtSign className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="text"
            value={kullaniciAdi}
            onChange={(e) => setKullaniciAdi(e.target.value.toLowerCase())}
            placeholder="kullanıcı adı"
            className="input-base pl-10 pr-28"
            autoComplete="username"
            required
            disabled={yukleniyor}
          />
          {!kullaniciAdi.includes('@') && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400 pointer-events-none">
              @{KULLANICI_DOMAIN}
            </span>
          )}
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Şifre
        </label>
        <div className="relative">
          <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <input
            type="password"
            value={sifre}
            onChange={(e) => setSifre(e.target.value)}
            placeholder="••••••••"
            className="input-base pl-10"
            autoComplete="current-password"
            required
            disabled={yukleniyor}
          />
        </div>
      </div>

      {hata && (
        <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-xl p-3">
          {hata}
        </div>
      )}

      {sifreUnutMesaj && (
        <div className="bg-green-50 border border-green-200 text-green-800 text-sm rounded-xl p-3">
          {sifreUnutMesaj}
        </div>
      )}

      <button
        type="submit"
        className="btn-primary w-full"
        disabled={!kullaniciAdi || !sifre || yukleniyor}
      >
        {yukleniyor ? 'Giriş yapılıyor...' : 'Giriş Yap'}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={sifremiUnuttumAc}
          disabled={sifreUnutGonderiliyor}
          className="text-sm text-brand-700 hover:text-brand-900 underline disabled:opacity-50"
        >
          Şifremi Unuttum
        </button>
      </div>

      {sifreModalAcik && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
          onClick={() => !sifreUnutGonderiliyor && setSifreModalAcik(false)}
        >
          <div
            className="bg-white rounded-2xl p-5 max-w-sm w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="font-bold text-lg mb-2">Şifremi Unuttum</h3>
            <p className="text-sm text-gray-600 mb-4">
              Hesabına bağlı e-posta adresini gir. Sıfırlama linki sana gönderilecek.
            </p>
            <input
              type="email"
              autoFocus
              value={sifreModalEmail}
              onChange={(e) => setSifreModalEmail(e.target.value)}
              placeholder="e-posta"
              className="input-base mb-3"
              disabled={sifreUnutGonderiliyor}
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setSifreModalAcik(false)}
                disabled={sifreUnutGonderiliyor}
                className="btn-secondary flex-1"
              >
                İptal
              </button>
              <button
                type="button"
                onClick={sifremiUnuttumGonder}
                disabled={sifreUnutGonderiliyor || !sifreModalEmail.trim()}
                className="btn-primary flex-1"
              >
                {sifreUnutGonderiliyor ? 'Gönderiliyor...' : 'Gönder'}
              </button>
            </div>
          </div>
        </div>
      )}
    </form>
  )
}
